import { useCallback, useEffect, useMemo, useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Popper,
  type PopperProps,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import dayjs from 'dayjs'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useSaveInvoiceBatchMutation,
  useInvoiceWarehousesLookupQuery,
  type InvoiceBatchRowIn,
} from '@/store/api/procurementApi'
import type { InvoiceLookupItem } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { roSx } from '../purchase/dialogStyle'

interface Props {
  open: boolean
  contractNo: string
  contractDate: string | null
  onClose: () => void
  onSaved: () => void
}

// ------------------------------------------------------------------ styling --
// Compact Excel-like grid — style bám 1:1 Goods Allocation › Mass Upload
// (GoodAllocationMassUploadDialog): font nhỏ, chiều cao ô cố định, header xám
// sticky, box viền, toolbar Copy/Delete/Reset, cột status ✓/✗.
const MASS_FIELD_H = 32
const massCellSx = { px: 0.5, py: 0.25 } as const
const massInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    height: MASS_FIELD_H,
    minHeight: MASS_FIELD_H,
    fontSize: '0.72rem',
    py: 0,
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-input': { py: 0, fontSize: '0.72rem' },
  // Autocomplete input có padding riêng → ép về 0 để cao bằng TextField.
  '& .MuiAutocomplete-input': { py: '0 !important', fontSize: '0.72rem' },
}
const massHeaderSx: SxProps<Theme> = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  fontSize: '0.7rem',
  lineHeight: 1.2,
  py: 0.5,
  px: 0.75,
  bgcolor: '#fafafa',
}
const computedHeaderSx: SxProps<Theme> = {
  ...massHeaderSx,
  bgcolor: '#eef2f6',
  color: 'text.secondary',
}
const computedCellSx: SxProps<Theme> = {
  ...massCellSx,
  fontSize: '0.72rem',
  color: 'text.secondary',
  fontWeight: 600,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  bgcolor: '#f7f9fb',
}
const toolbarBtnSx: SxProps<Theme> = {
  fontSize: '0.7rem',
  py: 0.25,
  px: 0.875,
  minHeight: 26,
  textTransform: 'none',
}
const massDialogPaperProps = {
  sx: {
    maxHeight: 'min(900px, 92vh)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 1,
  },
} as const
const massDialogTitleSx: SxProps<Theme> = {
  pb: 1,
  pt: 1.25,
  px: 2,
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  fontWeight: 700,
  fontSize: '1rem',
  lineHeight: 1.2,
}
const massTitleActionsBoxSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.5,
  alignItems: 'center',
  ml: 'auto',
  flexShrink: 0,
}
const massTitleActionBtnSx: SxProps<Theme> = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none',
}
const massDialogContentSx: SxProps<Theme> = {
  pt: 0.75,
  px: 2,
  pb: 1.5,
  overflowY: 'auto',
  flex: '1 1 auto',
  minHeight: 0,
  bgcolor: '#FFFFFF',
}

// ------------------------------------------------------------------ columns --
type ColKind = 'text' | 'date' | 'number'
type ColKey =
  | 'warehouse'
  | 'invoice_no'
  | 'invoice_date'
  | 'quantity'
  | 'price_vnd_kg'
  | 'total_amount'
  | 'vat_amount'
  | 'description'

interface ColDef {
  key: ColKey
  label: string
  kind: ColKind
  width: number
  required?: boolean
  /** Cột lookup → render Autocomplete (giống combobox Warehouse bên Page Invoice). */
  combo?: boolean
}

// Thứ tự + caption bám bảng nhập Access frmProcurementQryDetailsAddInvoice.
const COLUMNS: ColDef[] = [
  { key: 'warehouse', label: 'Warehouse', kind: 'text', width: 160, combo: true },
  { key: 'invoice_no', label: 'Invoice No', kind: 'text', width: 150, required: true },
  { key: 'invoice_date', label: 'Invoice Date', kind: 'date', width: 150, required: true },
  { key: 'quantity', label: 'Quantity', kind: 'number', width: 120 },
  { key: 'price_vnd_kg', label: 'Price (VND/KG)', kind: 'number', width: 130 },
  { key: 'total_amount', label: 'Total Amount', kind: 'number', width: 130 },
  { key: 'vat_amount', label: 'VAT Amount (INPUT)', kind: 'number', width: 140 },
  { key: 'description', label: 'Description', kind: 'text', width: 200 },
]

// 2 cột computed read-only (thay bảng preview "Check data" cũ) — tính realtime
// giống query ProcurementPay_WriteInvoice.
const COMPUTED_COLS: { label: string; width: number }[] = [
  { label: 'Total Amount (CAL)', width: 140 },
  { label: 'Invoice Amount (after tax)', width: 160 },
]

const HEADER_LABELS = COLUMNS.map((c) => c.label.toLowerCase())

/** Dòng dán là header (không phải data) khi ≥2 ô đầu khớp tên cột. */
function isHeaderRow(cells: string[]): boolean {
  let matches = 0
  cells.forEach((c, i) => {
    if (i < HEADER_LABELS.length && c.trim().toLowerCase() === HEADER_LABELS[i]) matches += 1
  })
  return matches >= 2
}

type RowDraft = Record<ColKey, string>

/** Ngày hiện tại dạng YYYY-MM-DD — default cho Invoice Date dòng mới (mirror
 *  Mass Upload set m_date=today: dòng "mới" luôn filled → validate + tô đỏ field
 *  bắt buộc còn trống NGAY khi mở form, thay vì "passed"). */
function todayIso(): string {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

let ROW_SEQ = 0
function emptyRow(): { id: number; data: RowDraft } {
  const data = {} as RowDraft
  COLUMNS.forEach((c) => {
    data[c.key] = ''
  })
  data.invoice_date = todayIso()
  return { id: ++ROW_SEQ, data }
}

function isRowFilled(data: RowDraft): boolean {
  return COLUMNS.some((c) => (data[c.key] ?? '').trim() !== '')
}

// ------------------------------------------------------------------ helpers --
/** Parse TSV từ clipboard → ma trận string[][]. */
function parseMatrix(text: string): string[][] {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n$/, '')
    .split('\n')
    .map((line) => line.split('\t'))
}

function buildIsoDate(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  if (year < 1900 || year > 2999) return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** Normalize ô ngày dán từ Excel → YYYY-MM-DD (ambiguous → D-M-Y kiểu VN). */
function parsePastedDate(raw: string): string {
  const s = raw.trim()
  if (!s) return ''
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (iso) {
    return buildIsoDate(+iso[1], +iso[2], +iso[3]) ?? buildIsoDate(+iso[1], +iso[3], +iso[2]) ?? ''
  }
  const sep = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/)
  if (sep) {
    const p1 = +sep[1]
    const p2 = +sep[2]
    const year = sep[3].length === 2 ? 2000 + +sep[3] : +sep[3]
    let day: number, month: number
    if (p1 > 12 && p2 <= 12) [day, month] = [p1, p2]
    else if (p2 > 12 && p1 <= 12) [day, month] = [p2, p1]
    else [day, month] = [p1, p2]
    return buildIsoDate(year, month, day) ?? ''
  }
  const t = new Date(s)
  if (!Number.isNaN(t.getTime())) return buildIsoDate(t.getFullYear(), t.getMonth() + 1, t.getDate()) ?? ''
  return ''
}

/** Chỉ feed YYYY-MM-DD hợp lệ cho <input type="date">. */
function safeDateValue(v: string): string {
  return /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v) ? v : ''
}

/** Parse raw input → number | null (blank/garbage → null, mirror Access Null). */
function parseNumeric(v: string): number | null {
  const s = v.replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

/** Format Standard (#,##0.00 — thousand sep + 2dp) khi blur ô số, giống mass-upload. */
function formatStandard(v: string): string {
  const n = parseNumeric(v)
  return n == null ? '' : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Format numeric hiển thị cột computed. */
function fmtNum(value: number | null): string {
  if (value === null) return ''
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Format date DD-MMM-YYYY (Access display). */
function fmtDate(value: string | null): string {
  if (!value) return ''
  const d = dayjs(value)
  return d.isValid() ? d.format('DD-MMM-YYYY') : ''
}

/** Total Amount (CAL) = Total Amount nếu nhập, else ROUND(price × quantity). */
function totalCal(data: RowDraft): number | null {
  const totalInput = parseNumeric(data.total_amount)
  if (totalInput !== null) return totalInput
  const price = parseNumeric(data.price_vnd_kg)
  const quantity = parseNumeric(data.quantity)
  return price !== null && quantity !== null ? Math.round(price * quantity) : null
}

/** Invoice Amount (after tax) = Nz(Total Amount (CAL),0) + Nz(VAT Amount,0). */
function invoiceAfterTax(data: RowDraft): number {
  return (totalCal(data) ?? 0) + (parseNumeric(data.vat_amount) ?? 0)
}

// ------------------------------------------------------------- cross-check --
// Per-row verdict — drives cột ✓/✗, ô đỏ, Alert panel và trạng thái enable của
// Save. Rule bám đúng path insert BE (ContractDetailInvoiceService.save_batch):
// dòng trống hoàn toàn bị drop trước insert (handleSave lọc isRowFilled) → pass;
// dòng có nhập PHẢI có Invoice No + Invoice Date + Total Amount (CAL) tính được,
// và Invoice No không được trùng trong batch. Dup ở DB (vendor) BE bắt lúc Save.
type Verdict = { ok: boolean; badFields: Set<ColKey>; messages: string[] }

function validateRow(data: RowDraft, dupNos: Set<string>): Verdict {
  const badFields = new Set<ColKey>()
  const messages: string[] = []
  if (!isRowFilled(data)) return { ok: true, badFields, messages }
  const fail = (field: ColKey, msg: string) => {
    badFields.add(field)
    messages.push(msg)
  }
  const invoiceNo = (data.invoice_no ?? '').trim()
  if (!invoiceNo) fail('invoice_no', 'Please type Invoice No !')
  else if (dupNos.has(invoiceNo.toLowerCase()))
    fail('invoice_no', 'Invoice No - Vendor is existed! Please type Other Invoice No !')
  if (!(data.invoice_date ?? '').trim()) fail('invoice_date', 'Please type Invoice Date !')
  if (totalCal(data) === null) fail('total_amount', 'Please type Total Payment !')
  const numFields: { key: ColKey; label: string }[] = [
    { key: 'quantity', label: 'Quantity' },
    { key: 'price_vnd_kg', label: 'Price (VND/KG)' },
    { key: 'total_amount', label: 'Total Amount' },
    { key: 'vat_amount', label: 'VAT Amount' },
  ]
  numFields.forEach(({ key, label }) => {
    const v = (data[key] ?? '').trim()
    if (v && parseNumeric(v) == null) fail(key, `Invalid ${label}`)
  })
  return { ok: badFields.size === 0, badFields, messages }
}

/** Popper cho ComboCell: dropdown rộng theo nội dung (value dài đọc đủ). */
function WideComboPopper(props: PopperProps) {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      style={{ ...props.style, width: 'fit-content', minWidth: 220, maxWidth: 480 }}
    />
  )
}

/** Cell combobox lookup Warehouse — Autocomplete flat giống Page Invoice: field
 *  chỉ hiện code, dropdown hiện "<CODE> — <NAME>". Value đã có (dán/paste) được
 *  inject vào options để không mất khi chưa khớp list. */
function ComboCell({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string
  options: InvoiceLookupItem[]
  onChange: (v: string) => void
  disabled: boolean
}) {
  const sel = options.find((o) => o.value === value) ?? (value ? { value, label: value } : undefined)
  const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size="small"
      fullWidth
      options={merged}
      value={sel}
      disabled={disabled}
      disableClearable
      onChange={(_e, v) => onChange(v?.value ?? '')}
      PopperComponent={WideComboPopper}
      getOptionLabel={(o) => (o ? String(o.value ?? '') : '')}
      renderOption={(props, o, state) => {
        const code = String(o.value ?? '')
        const lab = String(o.label ?? '')
        const text = !lab || lab === code ? code : `${code} — ${lab}`
        const { key, ...rest } = props as typeof props & { key?: React.Key }
        return (
          <li {...rest} key={`${code}__${lab}__${state.index}`} style={{ whiteSpace: 'nowrap' }}>
            {text}
          </li>
        )
      }}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      renderInput={(p) => <TextField {...p} sx={massInputSx} />}
    />
  )
}

/**
 * Contract Detail → Add Invoice dialog — converts Access
 * `frmProcurementQryDetailsAddInvoice` (caption "Procurement - Invoice").
 *
 * Batch nhập nhiều dòng invoice cho 1 hợp đồng (P-* / NPE-*). Grid Excel-like
 * (style Goods Allocation › Mass Upload): dán trực tiếp từ Excel (onPaste nở
 * dòng), Ctrl+V paste new rows, checkbox chọn dòng + Copy/Delete/Reset, cột
 * status ✓/✗ cross-check realtime, và 2 cột computed read-only (Total Amount
 * (CAL), Invoice Amount (after tax)). Save gọi POST invoice-batch — BE validate
 * exact Access messages rồi insert từng dòng trong một transaction.
 */
export default function ContractDetailAddInvoiceDialog({
  open,
  contractNo,
  contractDate,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const [saveBatch, { isLoading: isSaving }] = useSaveInvoiceBatchMutation()

  // Lookup Warehouse — dùng chung endpoint với combobox Warehouse bên Page Invoice.
  const { data: warehousesData } = useInvoiceWarehousesLookupQuery()
  const warehouseOptions = useMemo<InvoiceLookupItem[]>(
    () => warehousesData?.items ?? [],
    [warehousesData],
  )

  const [rows, setRows] = useState<{ id: number; data: RowDraft }[]>([emptyRow()])
  const [selected, setSelected] = useState<Set<number>>(() => new Set())

  // Re-init each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setRows([emptyRow()])
    setSelected(new Set())
  }, [open])

  const filledCount = useMemo(() => rows.filter((r) => isRowFilled(r.data)).length, [rows])
  const allSelected = rows.length > 0 && selected.size === rows.length

  // Invoice No trùng trong batch (≥2 lần) → tất cả bản trùng đều fail.
  const dupNos = useMemo(() => {
    const counts = new Map<string, number>()
    rows.forEach((r) => {
      const no = (r.data.invoice_no ?? '').trim().toLowerCase()
      if (no) counts.set(no, (counts.get(no) ?? 0) + 1)
    })
    const dup = new Set<string>()
    counts.forEach((n, k) => {
      if (n > 1) dup.add(k)
    })
    return dup
  }, [rows])

  const verdicts = useMemo(
    () => rows.map((r) => validateRow(r.data, dupNos)),
    [rows, dupNos],
  )
  const allValid = filledCount > 0 && verdicts.every((v) => v.ok)
  const failCount = verdicts.filter((v) => !v.ok).length
  const cellBg = (index: number, key: ColKey): string | undefined => {
    const v = verdicts[index]
    return v && !v.ok && v.badFields.has(key) ? 'rgba(211,47,47,0.14)' : undefined
  }

  /** Chuẩn hoá 1 ô dán theo loại cột (combo → khớp value trong list, không khớp
   *  bỏ trống; date → ISO; số → Standard; text → raw). */
  const coerceCell = useCallback(
    (col: ColDef, raw: string): string => {
      const v = (raw ?? '').trim()
      if (!v) return ''
      if (col.combo) {
        // Khớp option để chuẩn hoá; chưa khớp (options async / ngoài list) → giữ raw
        // để copy→paste không mất Warehouse (Autocomplete tự merge hiển thị).
        const hit = warehouseOptions.find((o) => String(o.value).toLowerCase() === v.toLowerCase())
        return hit ? String(hit.value) : v
      }
      if (col.kind === 'date') return parsePastedDate(v)
      if (col.kind === 'number') return formatStandard(v)
      return v
    },
    [warehouseOptions],
  )

  const setCell = (index: number, key: ColKey, value: string) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, data: { ...r.data, [key]: value } } : r)))

  const handleNumericBlur = (index: number, key: ColKey) => () =>
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, data: { ...r.data, [key]: formatStandard(r.data[key]) } } : r)),
    )

  /** Áp ma trận dán vào rows tại (startRow,startCol) — nở dòng theo vùng dán. */
  const applyMatrix = (
    prev: { id: number; data: RowDraft }[],
    startRow: number,
    startCol: number,
    matrix: string[][],
  ) => {
    const next = prev.map((r) => ({ id: r.id, data: { ...r.data } }))
    while (next.length < startRow + matrix.length) next.push(emptyRow())
    matrix.forEach((cells, r) => {
      const target = next[startRow + r]
      cells.forEach((raw, c) => {
        const fi = startCol + c
        if (fi >= COLUMNS.length) return
        target.data[COLUMNS[fi].key] = coerceCell(COLUMNS[fi], raw)
      })
    })
    return next
  }

  /** Dán tại ô (rowIdx,colIdx) — nở dòng theo vùng dán, điền theo thứ tự cột. */
  const handlePaste = (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData?.getData('text') ?? ''
    if (!text) return
    if (!text.includes('\t') && !text.includes('\n')) return
    e.preventDefault()
    e.stopPropagation()
    const matrix = parseMatrix(text)
    if (!matrix.length) return
    setRows((prev) => applyMatrix(prev, rowIdx, colIdx, matrix))
  }

  /** Append TSV block (Excel hoặc Copy của web) thành dòng mới ở CUỐI grid. */
  const appendRowsFromText = useCallback(
    (text: string): number => {
      const matrix = parseMatrix(text)
      if (!matrix.length) return 0
      const dataRows = matrix.length > 1 && isHeaderRow(matrix[0]) ? matrix.slice(1) : matrix
      if (!dataRows.length) return 0
      const built = dataRows.map((cells) => {
        const { id, data } = emptyRow()
        cells.forEach((raw, ci) => {
          if (ci >= COLUMNS.length) return
          data[COLUMNS[ci].key] = coerceCell(COLUMNS[ci], raw)
        })
        return { id, data }
      })
      setRows((prev) => {
        const pristine = prev.length === 1 && !isRowFilled(prev[0].data)
        return pristine ? built : [...prev, ...built]
      })
      return built.length
    },
    [coerceCell],
  )

  // Ctrl+V không nhắm vào ô cụ thể → paste as new rows (path tin cậy trên origin
  // http, nơi Clipboard read API không có).
  useEffect(() => {
    if (!open) return
    const onDocPaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }
      const text = e.clipboardData?.getData('text') ?? ''
      if (!text.trim()) return
      e.preventDefault()
      const added = appendRowsFromText(text)
      if (added > 0) snack.success(`Pasted ${added} new row${added === 1 ? '' : 's'}`)
    }
    document.addEventListener('paste', onDocPaste)
    return () => document.removeEventListener('paste', onDocPaste)
  }, [open, appendRowsFromText, snack])

  const addRow = () => setRows((prev) => [...prev, emptyRow()])

  const toggleSel = (index: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })

  const toggleSelAll = () =>
    setSelected((prev) => (prev.size === rows.length && rows.length > 0 ? new Set() : new Set(rows.map((_, i) => i))))

  const deleteSelected = () => {
    setRows((prev) => {
      const next = prev.filter((_, i) => !selected.has(i))
      return next.length ? next : [emptyRow()]
    })
    setSelected(new Set())
  }

  /** Xoá 1 dòng (nút delete đầu mỗi row) — reindex selected. */
  const deleteRow = (index: number) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index)
      return next.length ? next : [emptyRow()]
    })
    setSelected((prev) => {
      const next = new Set<number>()
      prev.forEach((i) => {
        if (i === index) return
        next.add(i > index ? i - 1 : i)
      })
      return next
    })
  }

  /** Copy bằng Selection API trên div contenteditable ẩn (chạy đúng trong Dialog
   *  FocusTrap, kể cả trên origin http). */
  const copyViaSelection = (html: string): boolean => {
    const div = document.createElement('div')
    div.contentEditable = 'true'
    div.innerHTML = html
    div.style.position = 'fixed'
    div.style.top = '-9999px'
    div.style.left = '-9999px'
    div.style.opacity = '0'
    document.body.appendChild(div)
    const selection = window.getSelection()
    if (!selection) {
      document.body.removeChild(div)
      return false
    }
    const savedRanges: Range[] = []
    for (let i = 0; i < selection.rangeCount; i++) savedRanges.push(selection.getRangeAt(i).cloneRange())
    const range = document.createRange()
    range.selectNodeContents(div)
    selection.removeAllRanges()
    selection.addRange(range)
    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch {
      ok = false
    }
    selection.removeAllRanges()
    savedRanges.forEach((r) => selection.addRange(r))
    document.body.removeChild(div)
    return ok
  }

  const copyViaTextarea = (text: string): boolean => {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.top = '-9999px'
    document.body.appendChild(ta)
    const prevActive = document.activeElement as HTMLElement | null
    ta.focus()
    ta.select()
    let ok = false
    try {
      ok = document.execCommand('copy')
    } catch {
      ok = false
    }
    document.body.removeChild(ta)
    prevActive?.focus?.()
    return ok
  }

  /** Copy dòng đã chọn (hoặc toàn bộ) → clipboard hệ thống dạng TSV+HTML. */
  const copyRows = async () => {
    const idx = selected.size > 0 ? [...selected].sort((a, b) => a - b) : rows.map((_, i) => i)
    const cellText = (i: number, c: ColDef) => String(rows[i].data[c.key] ?? '').replace(/[\t\r\n]/g, ' ')
    const headerRow = COLUMNS.map((c) => c.label).join('\t')
    const tsv = [headerRow, ...idx.map((i) => COLUMNS.map((c) => cellText(i, c)).join('\t'))].join('\n')
    const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const html =
      '<table>' +
      '<tr>' +
      COLUMNS.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('') +
      '</tr>' +
      idx
        .map((i) => '<tr>' + COLUMNS.map((c) => `<td>${escapeHtml(cellText(i, c))}</td>`).join('') + '</tr>')
        .join('') +
      '</table>'

    let ok = false
    try {
      const ClipboardItemCtor = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem
      if (ClipboardItemCtor && navigator.clipboard?.write) {
        const item = new ClipboardItemCtor({
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        })
        await navigator.clipboard.write([item])
        ok = true
      }
    } catch {
      ok = false
    }
    if (!ok) ok = copyViaSelection(html)
    if (!ok) {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(tsv)
          ok = true
        }
      } catch {
        ok = false
      }
    }
    if (!ok) ok = copyViaTextarea(tsv)

    if (ok) snack.success(`Copied ${idx.length} row${idx.length === 1 ? '' : 's'} — paste into the grid or Excel.`)
    else snack.error('Browser blocked clipboard access. Open the page over HTTPS or localhost.')
  }

  const resetForm = () => {
    setRows([emptyRow()])
    setSelected(new Set())
  }

  const handleSave = async () => {
    const filled = rows.filter((r) => isRowFilled(r.data))
    if (filled.length === 0) {
      snack.warning('Please input at least one Invoice row !')
      return
    }
    const firstBad = verdicts.findIndex((v) => !v.ok)
    if (firstBad >= 0) {
      snack.warning(`Row ${firstBad + 1}: ${verdicts[firstBad].messages[0]}`)
      return
    }
    const payload: InvoiceBatchRowIn[] = filled.map((r) => ({
      warehouse: r.data.warehouse.trim() || null,
      invoice_no: r.data.invoice_no.trim() || null,
      invoice_date: r.data.invoice_date || null,
      quantity: parseNumeric(r.data.quantity),
      price_vnd_kg: parseNumeric(r.data.price_vnd_kg),
      total_amount: parseNumeric(r.data.total_amount),
      vat_amount: parseNumeric(r.data.vat_amount),
      description: r.data.description.trim() || null,
    }))
    try {
      const res = await saveBatch({ contract_no: contractNo, rows: payload }).unwrap()
      snack.success(`Raw records: ${res.raw_records} — Errors: ${res.errors}`)
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save invoices'))
    }
  }

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      scroll="paper"
      PaperProps={massDialogPaperProps}
    >
      <DialogTitle sx={massDialogTitleSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
          <Box component="span" title="Procurement - Invoice">
            Inbound Logistics - Invoice
          </Box>
          <Box sx={massTitleActionsBoxSx}>
            <Tooltip title={failCount > 0 ? `${failCount} row(s) failed cross-check — fix to enable Save` : ''}>
              <span>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  color="success"
                  onClick={() => void handleSave()}
                  disabled={filledCount === 0 || isSaving || !allValid}
                  sx={massTitleActionBtnSx}
                >
                  {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
                </Button>
              </span>
            </Tooltip>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={handleClose}
              disabled={isSaving}
              sx={massTitleActionBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={massDialogContentSx}>
        <Stack spacing={1}>
          {/* Context row: Contract No | Date (read-only). */}
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            }}
          >
            <TextField
              label="Contract No"
              size="small"
              fullWidth
              value={contractNo}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
            <TextField
              label="Date"
              size="small"
              fullWidth
              value={fmtDate(contractDate)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Box>

          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: failCount > 0 ? 'error.main' : 'success.main',
              }}
            >
              {failCount > 0
                ? `Cross-check: ${failCount} of ${rows.length} row(s) need fixing`
                : `Cross-check passed — ${rows.length} row(s) ready`}
            </Typography>
            <Stack direction="row" spacing={0.75}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={addRow}
                disabled={isSaving}
                sx={toolbarBtnSx}
              >
                Add row
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={() => void copyRows()}
                sx={toolbarBtnSx}
              >
                {selected.size > 0 ? `Copy (${selected.size})` : 'Copy all'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                disabled={isSaving || selected.size === 0}
                onClick={deleteSelected}
                sx={toolbarBtnSx}
              >
                {selected.size > 0 ? `Delete (${selected.size})` : 'Delete selected'}
              </Button>
              <Button size="small" variant="text" onClick={resetForm} disabled={isSaving} sx={toolbarBtnSx}>
                Reset
              </Button>
            </Stack>
          </Stack>

          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ContentPasteIcon sx={{ fontSize: 14 }} />
            Copy rows in Excel (or click Copy), then press Ctrl+V to paste them as new rows. You can also paste directly
            into a cell.
          </Typography>

          <Box sx={{ maxHeight: '55vh', overflow: 'auto', border: '1px solid #bdbdbd', bgcolor: '#fff' }}>
            <Table size="small" stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...massHeaderSx, width: 44 }} />
                  <TableCell padding="checkbox" align="left" sx={{ bgcolor: '#fafafa', width: 44, pl: 0.75, pr: 2 }}>
                    <Checkbox
                      size="small"
                      indeterminate={selected.size > 0 && !allSelected}
                      checked={allSelected}
                      onChange={toggleSelAll}
                      disabled={isSaving}
                      sx={{ p: 0.25, ml: 0 }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...massHeaderSx, width: 36, textAlign: 'center', px: 0.25 }} title="Cross-check status">
                    ✓
                  </TableCell>
                  {COLUMNS.map((c, i) => (
                    <TableCell key={c.key} sx={{ ...massHeaderSx, width: c.width, ...(i === 0 ? { pl: 1.25 } : {}) }}>
                      {c.label}
                      {c.required ? ' *' : ''}
                    </TableCell>
                  ))}
                  {COMPUTED_COLS.map((c) => (
                    <TableCell key={c.label} sx={{ ...computedHeaderSx, width: c.width, textAlign: 'right' }}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, index) => {
                  const isSel = selected.has(index)
                  const verdict = verdicts[index]
                  const cal = totalCal(r.data)
                  const filled = isRowFilled(r.data)
                  return (
                    <TableRow key={r.id} hover selected={isSel}>
                      <TableCell sx={{ ...massCellSx, width: 44, verticalAlign: 'middle' }}>
                        <Tooltip title="Delete row">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isSaving || rows.length === 1}
                              onClick={() => deleteRow(index)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell padding="checkbox" align="left" sx={{ width: 44, pl: 0.75, pr: 2 }}>
                        <Checkbox
                          size="small"
                          checked={isSel}
                          onChange={() => toggleSel(index)}
                          disabled={isSaving}
                          sx={{ p: 0.25, ml: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...massCellSx, width: 36, textAlign: 'center', verticalAlign: 'middle', px: 0.25 }}>
                        {verdict?.ok ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 17, color: 'success.main', verticalAlign: 'middle' }} />
                        ) : (
                          <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{verdict?.messages.join('\n')}</Box>}>
                            <ErrorOutlineIcon sx={{ fontSize: 17, color: 'error.main', verticalAlign: 'middle' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      {COLUMNS.map((c, colIdx) => (
                        <TableCell
                          key={c.key}
                          sx={{ ...massCellSx, ...(colIdx === 0 ? { pl: 1.25 } : {}), bgcolor: cellBg(index, c.key) }}
                          onPasteCapture={(e) => handlePaste(index, colIdx, e)}
                        >
                          {c.combo ? (
                            <ComboCell
                              value={r.data[c.key]}
                              options={warehouseOptions}
                              onChange={(v) => setCell(index, c.key, v)}
                              disabled={isSaving}
                            />
                          ) : (
                            <TextField
                              value={c.kind === 'date' ? safeDateValue(r.data[c.key]) : r.data[c.key]}
                              onChange={(e) => setCell(index, c.key, e.target.value)}
                              onBlur={c.kind === 'number' ? handleNumericBlur(index, c.key) : undefined}
                              disabled={isSaving}
                              size="small"
                              fullWidth
                              type={c.kind === 'date' ? 'date' : 'text'}
                              InputLabelProps={c.kind === 'date' ? { shrink: true } : undefined}
                              inputProps={
                                c.kind === 'number'
                                  ? { style: { textAlign: 'right' }, inputMode: 'decimal' }
                                  : undefined
                              }
                              sx={massInputSx}
                            />
                          )}
                        </TableCell>
                      ))}
                      <TableCell sx={computedCellSx}>{filled ? fmtNum(cal) : ''}</TableCell>
                      <TableCell sx={computedCellSx}>{filled ? fmtNum(invoiceAfterTax(r.data)) : ''}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>

          {failCount > 0 && (
            <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.25 } }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
                Cross-check issues — fix before Save
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {verdicts.flatMap((v, i) =>
                  v.ok
                    ? []
                    : [
                        <Typography key={i} component="li" variant="caption" sx={{ display: 'list-item' }}>
                          {`Row ${i + 1}: ${v.messages.join(', ')}`}
                        </Typography>,
                      ],
                )}
              </Box>
            </Alert>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
